/**
 * File Service
 *
 * Centralized service for file upload, attachment, and management.
 * Handles CSRF tokens, progress tracking, and error handling.
 *
 * Uses model-specific backend endpoints for simplicity.
 */

import { getApiBaseUrl } from '@/lib/config';
import { getCSRFToken } from '@/lib/django';
import { fetchWithDefaults } from '@/lib/http/client';

// ============================================================================
// Types
// ============================================================================

export interface UploadedFile {
  id: number;
  filename?: string;
  original_filename: string;
  file_size: number;
  file_extension?: string;
  mime_type: string;
  file_url?: string;
  download_url?: string;
  created_at: string;
  // UI-only fields for upload progress tracking
  progress?: number;
  failed?: boolean;
}

/**
 * The URL to display an uploaded file at, or null when the API returned neither.
 *
 * `file_url` is preferred because it is the inline-viewing form (`download_url`
 * carries a `Content-Disposition: attachment`, which makes an `<img src>` useless).
 *
 * The host rewrite handles non-proxy local dev: there, vite proxies `/api` to the
 * Docker-internal `web:8000`, so Django's `build_absolute_uri` stamps that internal
 * host into `file_url` and the browser cannot resolve it. Under the local proxy and
 * in production the host is already correct and this is a no-op.
 */
export function resolveFileDisplayUrl(file: {
  // Deliberately wider than `UploadedFile`: the serializer returns `None` for
  // both fields when the row has no stored file, so `null` is a real value the
  // API emits, not just an absent key.
  file_url?: string | null;
  download_url?: string | null;
}): string | null {
  const url = file.file_url ?? file.download_url;
  if (url == null || url === '') {
    return null;
  }
  return url.replace(/^https?:\/\/web:\d+/, window.location.origin);
}

export interface FileUploadOptions {
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}

// ============================================================================
// Custom Errors
// ============================================================================

export class FileUploadError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly response?: unknown
  ) {
    super(message);
    this.name = 'FileUploadError';
  }
}

// ============================================================================
// File Service Class
// ============================================================================

/**
 * Service for file upload and attachment operations
 */
class FileService {
  /**
   * Upload a file to the server
   *
   * Note: This method uses XMLHttpRequest instead of fetchWithDefaults
   * because it requires progress tracking which fetch() doesn't support.
   *
   * @param orgId - Organization ID
   * @param file - File to upload
   * @param options - Upload options (progress callback, abort signal)
   * @returns Promise resolving to uploaded file metadata
   */
  async uploadFile(
    orgId: number,
    file: File,
    options: FileUploadOptions = {}
  ): Promise<UploadedFile> {
    const { onProgress, signal } = options;
    const API_BASE_URL = getApiBaseUrl();

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.timeout = 300000; // 5-minute cap for large file uploads

      // Handle abort signal
      if (signal) {
        signal.addEventListener('abort', () => {
          xhr.abort();
          reject(new FileUploadError('Upload cancelled by user'));
        });
      }

      // Progress tracking
      if (onProgress) {
        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable) {
            const progress = (e.loaded / e.total) * 100;
            onProgress(progress);
          }
        });
      }

      // Success handler
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch {
            reject(new FileUploadError('Invalid JSON response from server'));
          }
        } else {
          let errorMessage = `Upload failed with status ${xhr.status}`;
          try {
            const errorData = JSON.parse(xhr.responseText);
            errorMessage =
              (Array.isArray(errorData.file) && errorData.file[0]) ||
              errorData.detail ||
              errorData.error ||
              errorMessage;
          } catch {
            // Use default error message
          }
          reject(
            new FileUploadError(errorMessage, xhr.status, xhr.responseText)
          );
        }
      });

      // Error handler
      xhr.addEventListener('error', () => {
        reject(new FileUploadError('Network error during file upload'));
      });

      // Abort handler
      xhr.addEventListener('abort', () => {
        reject(new FileUploadError('Upload cancelled'));
      });

      // Timeout handler
      xhr.addEventListener('timeout', () => {
        reject(
          new FileUploadError(
            'Upload timed out – file may be too large or connection too slow',
            408
          )
        );
      });

      // Prepare request
      const formData = new FormData();
      formData.append('file', file);
      formData.append('org', orgId.toString());

      const csrfToken = getCSRFToken();
      if (!csrfToken) {
        reject(new FileUploadError('CSRF token not found'));
        return;
      }

      // Send request
      xhr.open('POST', `${API_BASE_URL}/api/orgs/${orgId}/files/`);
      xhr.setRequestHeader('X-CSRFToken', csrfToken);
      xhr.withCredentials = true;
      xhr.send(formData);
    });
  }

  /**
   * Attach a file to a CRM record
   *
   * @param orgId - Organization ID
   * @param uploadedFileId - ID of uploaded file
   * @param targetEntityId - Entity ID of the CRM record to attach to
   * @param associationTypeName - Association type name (e.g. 'ahoy_file_to_contact')
   * @returns Promise resolving to the created file entity
   */
  async attachToRecord(
    orgId: number,
    uploadedFileId: number,
    targetEntityId: string,
    associationTypeName: string
  ): Promise<{ entity_id: string }> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/crm-events/files/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uploaded_file_id: uploadedFileId,
          target_entity_id: targetEntityId,
          association_type_name: associationTypeName,
        }),
      }
    );

    if (!response.ok) {
      const rawText = await response.text();
      let errorMessage = `Failed to attach file to record (status ${response.status})`;
      try {
        const errorData = JSON.parse(rawText);
        errorMessage = errorData.detail || errorData.error || errorMessage;
      } catch {
        // Use default error message
      }
      throw new FileUploadError(errorMessage, response.status, rawText);
    }

    return response.json();
  }

  /**
   * Mint an `ahoy_file` CRM entity with no record association.
   *
   * Unlike `attachToRecord`, this posts only `uploaded_file_id` — the entity
   * is created with owner + org-level visibility but no `target_entity_id`/
   * `association_type_name`. Used by the create-form Files picker, where a
   * file must exist as a CRM entity before the record it belongs to is saved.
   *
   * @param orgId - Organization ID
   * @param uploadedFileId - ID of uploaded file
   * @returns Promise resolving to the created file entity
   */
  async mintFileEntity(
    orgId: number,
    uploadedFileId: number
  ): Promise<{ entity_id: string }> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/crm-events/files/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uploaded_file_id: uploadedFileId,
        }),
      }
    );

    if (!response.ok) {
      const rawText = await response.text();
      let errorMessage = `Failed to upload file (status ${response.status})`;
      try {
        const errorData = JSON.parse(rawText);
        errorMessage = errorData.detail || errorData.error || errorMessage;
      } catch {
        // Use default error message
      }
      throw new FileUploadError(errorMessage, response.status, rawText);
    }

    return response.json();
  }

  /**
   * Upload a file and attach it to a CRM record in one operation
   *
   * @param orgId - Organization ID
   * @param file - File to upload
   * @param targetEntityId - Entity ID of the CRM record to attach to
   * @param associationTypeName - Association type name (e.g. 'ahoy_file_to_contact')
   * @param options - Upload options (progress callback, abort signal)
   * @returns Promise resolving to uploaded file metadata and the created file entity ID
   */
  async uploadAndAttachToRecord(
    orgId: number,
    file: File,
    targetEntityId: string,
    associationTypeName: string,
    options: FileUploadOptions = {}
  ): Promise<{ uploadedFile: UploadedFile; entityId: string }> {
    // Upload file first
    const uploadedFile = await this.uploadFile(orgId, file, options);

    // Attach to CRM record
    const result = await this.attachToRecord(
      orgId,
      uploadedFile.id,
      targetEntityId,
      associationTypeName
    );

    return { uploadedFile, entityId: result.entity_id };
  }

  /**
   * Attach a file to a conversation
   *
   * @param orgId - Organization ID
   * @param fileId - ID of uploaded file
   * @param conversationId - ID of conversation to attach to
   * @returns Promise resolving to attachment response
   */
  async attachToConversation(
    orgId: number,
    fileId: number,
    conversationId: number
  ): Promise<{ id: number; file: UploadedFile }> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/conversations/${conversationId}/files/${fileId}/attach/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.text();
      throw new FileUploadError(
        `Failed to attach file: ${errorData}`,
        response.status,
        errorData
      );
    }

    return response.json();
  }

  /**
   * Upload a file and attach it to a conversation in one operation
   *
   * @param orgId - Organization ID
   * @param file - File to upload
   * @param conversationId - ID of conversation to attach to
   * @param options - Upload options (progress callback, abort signal)
   * @returns Promise resolving to uploaded file metadata
   */
  async uploadAndAttachToConversation(
    orgId: number,
    file: File,
    conversationId: number,
    options: FileUploadOptions = {}
  ): Promise<UploadedFile> {
    // Upload file first
    const uploadedFile = await this.uploadFile(orgId, file, options);

    // Attach to conversation
    await this.attachToConversation(orgId, uploadedFile.id, conversationId);

    return uploadedFile;
  }

  /**
   * Get files attached to a proposal
   *
   * @param orgId - Organization ID
   * @param proposalId - ID of proposal
   * @returns Promise resolving to list of files
   */
  async getProposalFiles(
    orgId: number,
    proposalId: number
  ): Promise<UploadedFile[]> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/proposals/${proposalId}/files/`
    );

    if (!response.ok) {
      const errorData = await response.text();
      throw new FileUploadError(
        `Failed to fetch files: ${errorData}`,
        response.status,
        errorData
      );
    }

    return response.json();
  }

  /**
   * Attach a file to a proposal
   *
   * @param orgId - Organization ID
   * @param fileId - ID of uploaded file
   * @param proposalId - ID of proposal to attach to
   * @returns Promise resolving to attachment response
   */
  async attachToProposal(
    orgId: number,
    fileId: number,
    proposalId: number
  ): Promise<{ id: number; file: UploadedFile }> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/proposals/${proposalId}/files/${fileId}/attach/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.text();
      throw new FileUploadError(
        `Failed to attach file: ${errorData}`,
        response.status,
        errorData
      );
    }

    return response.json();
  }

  /**
   * Upload a file and attach it to a proposal in one operation
   *
   * @param orgId - Organization ID
   * @param file - File to upload
   * @param proposalId - ID of proposal to attach to
   * @param options - Upload options (progress callback, abort signal)
   * @returns Promise resolving to uploaded file metadata
   */
  async uploadAndAttachToProposal(
    orgId: number,
    file: File,
    proposalId: number,
    options: FileUploadOptions = {}
  ): Promise<UploadedFile> {
    // Upload file first
    const uploadedFile = await this.uploadFile(orgId, file, options);

    // Attach to proposal
    await this.attachToProposal(orgId, uploadedFile.id, proposalId);

    return uploadedFile;
  }

  /**
   * Detach a file from a proposal
   *
   * @param orgId - Organization ID
   * @param proposalId - ID of proposal
   * @param fileId - ID of file to detach
   */
  async detachFromProposal(
    orgId: number,
    proposalId: number,
    fileId: number
  ): Promise<void> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/proposals/${proposalId}/files/${fileId}/detach/`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      const errorData = await response.text();
      throw new FileUploadError(
        `Failed to detach file: ${errorData}`,
        response.status,
        errorData
      );
    }
  }

  /**
   * Import files from Google Drive
   *
   * @param orgId - Organization ID
   * @param fileIds - Array of Google Drive file IDs to import
   * @returns Promise resolving to imported files and any errors
   */
  async importFromDrive(
    orgId: number,
    fileIds: string[]
  ): Promise<{
    files: UploadedFile[];
    errors: Array<{ file_id: string; error: string }>;
  }> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/files/from-drive/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_ids: fileIds }),
      }
    );

    if (!response.ok) {
      const errorData = await response.text();
      throw new FileUploadError(
        `Failed to import from Drive: ${errorData}`,
        response.status,
        errorData
      );
    }

    return response.json();
  }

  /**
   * Look up the UploadedFile linked to a CRM file entity.
   *
   * @param orgId - Organization ID
   * @param entityId - CRM entity UUID of the ahoy_file entity
   * @returns The first matching UploadedFile, or null if none found
   */
  async getByEntityId(
    orgId: number,
    entityId: string
  ): Promise<UploadedFile | null> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/files/?entity_id=${encodeURIComponent(entityId)}`
    );

    if (!response.ok) {
      throw new FileUploadError(
        'Failed to look up file by entity ID',
        response.status
      );
    }

    const data: UploadedFile[] | { results?: UploadedFile[] } =
      await response.json();
    // The list endpoint returns a plain array (not paginated).
    const results = Array.isArray(data) ? data : (data.results ?? []);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Delete a file from the server
   *
   * @param orgId - Organization ID
   * @param fileId - ID of file to delete
   */
  async deleteFile(orgId: number, fileId: number): Promise<void> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/files/${fileId}/`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      const errorData = await response.text();
      throw new FileUploadError(
        `Failed to delete file: ${errorData}`,
        response.status,
        errorData
      );
    }
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const fileService = new FileService();
